// ✨ Modern Create Group Modal UI
// Matching the app's clean, modern design aesthetic

import React, { useState } from 'react';
import Portal from './Portal';
import imageCompression from 'browser-image-compression';
import { v4 as uuidv4 } from 'uuid';
import { safeFetch } from './App';
import { memoryCache } from "./cacheSafeFetch";
import { Users, Image, Link, Tag, X, Plus } from 'lucide-react';

export default function CreateGroupModal({ chatId, zone, allCategories, setShowCreateGroupModal, setRefreshKey }) {
    const [groupForm, setGroupForm] = useState({
        name: '',
        description: '',
        imageFile: null,
        imagePreview: '',
        websiteLinks: [{ name: '', url: '' }],
        chatLinks: [{ name: '', url: '' }],
    });
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [customCategory, setCustomCategory] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [categorySearch, setCategorySearch] = useState('');

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGroupForm({ ...groupForm, imageFile: file });
            const reader = new FileReader();
            reader.onloadend = () => setGroupForm((prev) => ({ ...prev, imagePreview: reader.result }));
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        const { name, description, websiteLinks, chatLinks, imageFile } = groupForm;
        const hasLink = websiteLinks.some(l => l.url.trim()) || chatLinks.some(l => l.url.trim());

        if (!name.trim() || !description.trim() || !hasLink) {
            alert('יש למלא שם, תיאור, ולספק לפחות קישור אחד לאתר או לצ׳אט');
            return;
        }

        try {
            setSubmitting(true);
            let imageKey = null;

            if (imageFile) {
                const uuid = uuidv4();
                const baseName = imageFile.name.replace(/_thumb|\.[^/.]+$/g, '');

                const fullCompressed = await imageCompression(imageFile, {
                    maxSizeMB: 0.5,
                    maxWidthOrHeight: 1280,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });
                const fullName = `${uuid}_${baseName}.jpg`;
                const fullFile = new File([fullCompressed], fullName, { type: 'image/jpeg' });

                const thumbCompressed = await imageCompression(imageFile, {
                    maxSizeMB: 0.1,
                    maxWidthOrHeight: 200,
                    useWebWorker: true,
                    fileType: 'image/jpeg',
                });
                const thumbName = `${uuid}_${baseName}_thumb.jpg`;
                const thumbFile = new File([thumbCompressed], thumbName, { type: 'image/jpeg' });

                const allFiles = [
                    { file: fullFile, name: fullName, type: 'image/jpeg' },
                    { file: thumbFile, name: thumbName, type: 'image/jpeg' },
                ];

                const presignRes = await safeFetch(
                    'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/upload-urls',
                    chatId,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            filenames: allFiles.map(f => f.name),
                            content_types: allFiles.map(f => f.type),
                            chat_id: chatId,
                        }),
                    }
                );

                const { urls } = await presignRes.json();
                if (!urls || urls.length !== allFiles.length) throw new Error('Mismatch in presigned URL count');

                for (let i = 0; i < allFiles.length; i++) {
                    const { file } = allFiles[i];
                    const { url, fields } = urls[i];
                    const formData = new FormData();
                    Object.entries(fields).forEach(([key, val]) => formData.append(key, val));
                    formData.append('file', file);
                    const uploadRes = await fetch(url, { method: 'POST', body: formData });
                    if (!uploadRes.ok) throw new Error('Upload failed');
                }
                imageKey = fullName;
            }

            const finalCategories = [...selectedCategories, ...(customCategory.trim() ? [customCategory.trim()] : [])];

            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/create-group',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        zone_id: zone,
                        name,
                        description,
                        chat_links: groupForm.chatLinks.filter(l => l.url.trim()),
                        website_links: groupForm.websiteLinks.filter(l => l.url.trim()),
                        image_key: imageKey,
                        categories: finalCategories
                    }),
                }
            );

            if (!res.ok) {
                alert('❌ שגיאה ביצירת הקבוצה');
                return;
            }

            setShowCreateGroupModal(false);
            setRefreshKey(prev => prev + 1);

            delete memoryCache[`get-zone-groups-${zone}-${chatId}`]
        } catch (err) {
            console.error('❌ Group creation error:', err);
            alert('שגיאה כללית בשליחה');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Portal>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in z-[100001]" dir="rtl">
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-line px-8 py-6 rounded-t-3xl z-10 text-right">
                        <button
                            onClick={() => setShowCreateGroupModal(false)}
                            className="absolute top-4 left-4 w-9 h-9 rounded-xl bg-surface-muted hover:bg-surface-sunken text-ink-muted font-bold flex items-center justify-center transition-all"
                        >
                            <X size={20} />
                        </button>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary-500 to-brand-400 flex items-center justify-center">
                                <Users size={24} className="text-white" />
                            </div>
                            <h2 className="text-2xl font-bold text-ink-strong">קבוצה חדשה</h2>
                        </div>
                        <div className="bg-secondary-50 border border-secondary-500/30 rounded-2xl p-4 space-y-2">
                            <p className="text-sm text-ink-muted leading-relaxed">
                                קבוצות המקומית נועדו להקל על גילוי קבוצות שיח בכל נושא בקהילות שלכם. תוכלו להציג את קבוצות הוואטסאפ, טלגרם או כל קישור לקבוצה שלכם.
                            </p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-8 py-6 space-y-6 text-right">
                        {/* Name */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm font-bold text-ink-strong">
                                <Users size={16} className="text-secondary-500" />
                                שם הקבוצה *
                            </label>
                            <input
                                type="text"
                                value={groupForm.name}
                                onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                                className="w-full border-2 border-line rounded-2xl p-3 text-sm text-ink-strong placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 bg-surface-muted"
                                placeholder="למשל: שכנים רעבים"
                            />
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm font-bold text-ink-strong">
                                <Tag size={16} className="text-secondary-500" />
                                תיאור הקבוצה *
                            </label>
                            <textarea
                                value={groupForm.description}
                                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                                rows={4}
                                className="w-full border-2 border-line rounded-2xl p-4 text-sm text-ink-strong placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 bg-surface-muted resize-none"
                                placeholder="ספרו על מטרת הקבוצה..."
                            />
                        </div>

                        {/* Image Upload */}
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm font-bold text-ink-strong">
                                <Image size={16} className="text-secondary-500" />
                                תמונת קבוצה (אופציונלי)
                            </label>
                            <div className="relative">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                    id="group-image-upload"
                                />
                                <label
                                    htmlFor="group-image-upload"
                                    className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-line-strong rounded-2xl p-4 text-sm text-ink-muted hover:border-secondary-500 hover:bg-surface-muted transition-all cursor-pointer"
                                >
                                    <Image size={20} className="text-secondary-500" />
                                    {groupForm.imageFile ? groupForm.imageFile.name : 'בחר תמונה'}
                                </label>
                            </div>
                            {groupForm.imagePreview && (
                                <div className="relative rounded-2xl overflow-hidden border-2 border-line">
                                    <img
                                        src={groupForm.imagePreview}
                                        alt="תצוגה מקדימה"
                                        className="w-full h-48 object-cover"
                                    />
                                    <button
                                        onClick={() => setGroupForm({ ...groupForm, imageFile: null, imagePreview: '' })}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-danger-500 hover:bg-danger-600 text-white flex items-center justify-center transition-all"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-line"></div>

                        {/* Chat Links */}
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm font-bold text-ink-strong">
                                <Link size={16} className="text-secondary-500" />
                                קישור לצ'אט *
                            </label>
                            <div className="space-y-3">
                                {groupForm.chatLinks.map((link, idx) => (
                                    <div key={idx} className="bg-surface-muted rounded-2xl p-4 space-y-3 border border-line">
                                        <div className="flex items-center justify-between">
                                            {groupForm.chatLinks.length > 1 && (
                                                <button
                                                    onClick={() => {
                                                        const updated = groupForm.chatLinks.filter((_, i) => i !== idx);
                                                        setGroupForm({ ...groupForm, chatLinks: updated });
                                                    }}
                                                    className="text-danger-500 hover:text-danger-600 text-xs font-bold flex items-center gap-1"
                                                >
                                                    <X size={14} />
                                                    הסר
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="שם התצוגה (למשל: קישור הצטרפות)"
                                            value={link.name}
                                            onChange={(e) => {
                                                const updated = [...groupForm.chatLinks];
                                                updated[idx].name = e.target.value;
                                                setGroupForm({ ...groupForm, chatLinks: updated });
                                            }}
                                            className="w-full border-2 border-line rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                                        />
                                        <input
                                            type="url"
                                            placeholder="https://chat.whatsapp.com/... או https://t.me/..."
                                            value={link.url}
                                            onChange={(e) => {
                                                const updated = [...groupForm.chatLinks];
                                                updated[idx].url = e.target.value;
                                                setGroupForm({ ...groupForm, chatLinks: updated });
                                            }}
                                            className="w-full border-2 border-line rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-line"></div>

                        {/* Categories */}
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm font-bold text-ink-strong">
                                <Tag size={16} className="text-secondary-500" />
                                קטגוריות (עד 3)
                            </label>

                            {/* Search */}
                            <input
                                type="text"
                                value={categorySearch}
                                onChange={(e) => setCategorySearch(e.target.value)}
                                placeholder="חפש קטגוריה..."
                                className="w-full border-2 border-line rounded-2xl p-3 text-sm text-ink-strong placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 bg-surface-muted"
                            />

                            {/* Category Grid */}
                            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-surface-muted rounded-2xl border border-line">
                                {allCategories
                                    .filter((cat) =>
                                        cat.toLowerCase().includes(categorySearch.toLowerCase())
                                    )
                                    .map((cat) => {
                                        const isSelected = selectedCategories.includes(cat);
                                        const disabled = !isSelected && selectedCategories.length >= 3;
                                        return (
                                            <label
                                                key={cat}
                                                className={`flex items-center gap-2 p-3 rounded-xl text-sm cursor-pointer transition-all ${isSelected
                                                    ? 'bg-secondary-500 text-white font-semibold shadow-sm'
                                                    : 'bg-white text-ink-muted border border-line hover:border-secondary-500 hover:bg-surface-muted'
                                                    } ${disabled && !isSelected ? 'opacity-40 cursor-not-allowed' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={disabled}
                                                    onChange={() => {
                                                        if (isSelected) {
                                                            setSelectedCategories(
                                                                selectedCategories.filter((c) => c !== cat)
                                                            );
                                                        } else if (selectedCategories.length < 3) {
                                                            setSelectedCategories([
                                                                ...selectedCategories,
                                                                cat,
                                                            ]);
                                                        }
                                                    }}
                                                    className="w-4 h-4 rounded accent-secondary-500"
                                                />
                                                <span className="flex-1 truncate">{cat}</span>
                                            </label>
                                        );
                                    })}
                            </div>

                            {/* Custom Category */}
                            <input
                                type="text"
                                value={customCategory}
                                onChange={(e) => setCustomCategory(e.target.value)}
                                className="w-full border-2 border-line rounded-2xl p-3 text-sm text-ink-strong placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 bg-surface-muted"
                                placeholder="או הזן קטגוריה מותאמת אישית"
                            />
                        </div>

                        {/* Divider */}
                        <div className="border-t border-line"></div>

                    </div>

                    {/* Footer */}
                    <div className="sticky bottom-0 bg-white border-t border-line px-8 py-6 rounded-b-3xl text-right">
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCreateGroupModal(false)}
                                className="flex-1 px-6 py-3 rounded-2xl bg-surface-muted hover:bg-surface-sunken text-ink-strong font-bold transition-all active:scale-95"
                            >
                                ביטול
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="flex-1 px-6 py-3 rounded-2xl bg-secondary-500 hover:bg-secondary-600 text-white font-bold transition-all shadow-soft hover:shadow-card active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? 'יוצר...' : 'צור קבוצה'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
}
